import { MAX_FILE_SIZE } from '@common/constants/client'
import { CLIENT_TAB_ID_HEADER } from '@common/constants/webSockets'
import { humanReadableFileSize } from '@common/helpers/helpers'
import qs from 'qs'
import superagent from 'superagent'
import { CLIENT_TAB_ID } from '../helpers/clientTabId'
import { prepareRequestParams } from '../helpers/helpers'
import { removeToken, shouldAdoptRotatedToken } from '../helpers/tokenUtils'

/** A relative app path (never another origin), e.g. a merged CRM record's survivor page. */
let notFoundRedirect: string | null = null

const sameOriginPath = (value: unknown): string | null =>
  typeof value === 'string' && /^\/(?![/\\])/.test(value) ? value : null

/**
 * Where the server puts a re-issued token (`SESSION_TOKEN_HEADER` on the API side). Lowercase
 * because that is how Node normalises header names and how superagent keys its map.
 */
const SESSION_TOKEN_HEADER = 'x-session-token'

// Error payload nested under `body.error` on a failed response, and the shape
// merged onto the thrown Error so sagas/middleware can read `code`/`status`.
interface IApiErrorData {
  code: string
  message: string
  status?: number
  /** Per-field validation reasons, or `redirectTo` on a merged CRM record's ID_NOT_FOUND. */
  data?: { redirectTo?: unknown } & Record<string, unknown>
  /**
   * Which token this request went out with — as a generation counter, never the token itself.
   * The error object is handed to reducers and lives in redux state, so a JWT on it could reach
   * devtools or telemetry; a counter answers the only question anyone asks of it ("is the token
   * we hold still the one that failed?") and discloses nothing.
   */
  tokenGeneration?: number
}

// Precise shape of the response body our backend returns. Success responses
// carry `data`; failures carry `error`. In the file (blob) branch the response
// body is the binary payload, onto which we attach a `name` for downloads.
interface IApiResponseBody {
  // The response payload shape varies per endpoint; consumers type it via
  // createAction/sagas, so this stays `any` (the API boundary) — narrowing it
  // to `unknown` breaks every typed consumer of apiClient.get/post.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data?: any
  error?: IApiErrorData
  name?: string
}

// The slice of the superagent response object we actually read. Typed instead
// of `any` so member access is checked. `headers` is superagent's index map.
interface IApiResponse {
  status: number
  headers: { [key: string]: string }
  body: IApiResponseBody
}

// Ad-hoc sentinel flag set on `window` to suppress duplicate error redirects.
interface IRedirectingWindow extends Window {
  __REDIRECTING_TO_ERROR?: boolean
}

export default class ApiClient {
  prefix: string
  token: string | null
  /**
   * Bumped every time `token` changes. Lets a reply be matched against the token that sent it:
   * a 401 is only evidence that *the token the request carried* is dead, and by the time it
   * lands the tab may be holding a different, healthy one — a login completing mid-flight is
   * enough (`setToken` runs on every login path). Acting on the token in hand instead is how a
   * straggler signs the user out of the session they just created.
   */
  tokenGeneration = 0

  constructor(prefix: string) {
    if (!prefix) {
      throw new Error('[apiPrefix] required')
    }
    this.prefix = prefix
    this.token = window.localStorage.getItem('token') || null
  }

  setToken(token: string) {
    this.token = token
    this.tokenGeneration += 1
    window.localStorage.setItem('token', token)
  }

  /**
   * Forget this tab's token.
   *
   * `onlyIfCurrent` keeps a tab from clearing a token that is not its own. `localStorage` is
   * shared by every tab of the profile while `this.token` is per-tab, and sessions slide
   * (server: services/auth/sessionRotation) — so another tab may have rotated a newer, healthy
   * token into storage since this one started. A tab signing itself out because *its* token was
   * refused must leave that one alone; deleting it signs the healthy tab out on its next
   * reload, which is the same cross-tab damage this whole change is about, just moved from the
   * server's denylist into the browser's storage.
   *
   * A deliberate sign-out passes nothing and clears storage outright, which is correct: it
   * revokes the whole session server-side, so every tab's token is dead anyway.
   */
  removeToken({ onlyIfCurrent = false }: { onlyIfCurrent?: boolean } = {}) {
    const storedToken = window.localStorage.getItem('token')
    const isOurs = storedToken !== null && storedToken === this.token

    this.token = null
    this.tokenGeneration += 1

    if (!onlyIfCurrent || isOurs) {
      removeToken()
    }
  }

  get(url: string, params: object = {}) {
    return this._request({ url, method: 'get', params })
  }

  post(url: string, body: object = {}, params: object = {}) {
    return this._request({ url, method: 'post', body, params })
  }

  patch(url: string, body: object = {}, params: object = {}) {
    return this._request({ url, method: 'patch', body, params })
  }

  put(url: string, body: object = {}) {
    return this._request({ url, method: 'put', body })
  }

  delete(url: string, body: object = {}) {
    return this._request({ url, method: 'delete', body })
  }

  async beacon(url: string, body: object = {}) {
    const headers: { [key: string]: string } = {
      'Content-Type': 'application/json',
    }

    // Only add Authorization header if token exists
    if (this.token) {
      headers.Authorization = `Bearer ${this.token}`
    }

    const data = await fetch(`${this.prefix}${url}`, {
      keepalive: true,
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    })
    return data
  }

  _request({
    url,
    method,
    params = {},
    body,
  }: {
    url: string
    method: 'get' | 'post' | 'patch' | 'put' | 'delete'
    params?: object & { isFile?: boolean }
    body?: object
  }) {
    // Just in case the url has double slashes, we need to replace them with single slashes
    const finalUrl = `${this.prefix}${url}`.replaceAll('//', '/')
    const req = superagent[method](finalUrl).timeout({
      response: 5 * 60 * 1000,
      deadline: 5 * 60 * 1000,
    })

    const { isFile } = params || {}
    if (isFile) {
      req.responseType('blob')
      delete params.isFile
    }

    if (params && Object.keys(params).length > 0) {
      // Serialize params using qs to properly handle nested objects with arrays
      // Express on the backend uses qs with these same options to parse
      // Using 'indices' format: filters[clientName][0]=val1&filters[clientName][1]=val2
      const serialized = qs.stringify(params, {
        arrayFormat: 'indices',
        encode: true,
      })
      req.query(serialized)
    }
    if (body) {
      req.send(prepareRequestParams(body as Record<string, unknown>))
    }
    if (this.token) {
      req.set('Authorization', `Bearer ${this.token}`)
    }
    if (CLIENT_TAB_ID) {
      req.set(CLIENT_TAB_ID_HEADER, CLIENT_TAB_ID)
    }

    // Prevent superagent from throwing on HTTP errors, so we can check status codes manually
    req.ok(() => true)

    // The token this request is being sent with, read before it leaves. What comes back is
    // only a replacement for *this* token — see the guard below.
    const sentWith = this.token
    // ...and which generation that was, travelling out on any error so the handler can tell a
    // reply about the current token from one about a token that has since been replaced.
    const sentWithGeneration = this.tokenGeneration

    return req.then((res: IApiResponse) => {
      // Sessions slide: the server answers a request carrying an ageing token with a fresh one
      // in this header, and taking it here — on the one path every request goes through — is
      // what keeps somebody who uses Atlas from being signed out on a schedule. Missing it is
      // harmless: the token in hand keeps working until it expires, which is how this behaved
      // before rotation existed.
      //
      // Only adopted if the token has not moved on while this request was in flight. A slow
      // request outlives the state it set off in: sign out clears the token and navigates
      // within the app rather than reloading it, so a reply landing afterwards used to write a
      // working token back into storage — and the login page, finding one, signed the user
      // straight back in. The same staleness could restore a `clientId` a portfolio user had
      // just stepped out of.
      //
      // And only adopted if it is actually newer than the token in hand. The browser serves
      // cached API answers with their original headers, so this header can carry a token that
      // was fresh when the response was first stored and is long dead now — see
      // `shouldAdoptRotatedToken` for what that did to people.
      const freshToken = res.headers?.[SESSION_TOKEN_HEADER]
      if (
        freshToken &&
        this.token === sentWith &&
        shouldAdoptRotatedToken(freshToken, this.token)
      ) {
        this.setToken(freshToken)
      }

      if (isFile) {
        const fileName = res.headers['content-disposition']?.split('filename=')[1]?.split(';')[0]
        if (fileName) {
          res.body.name = fileName.replaceAll('"', '')
        }
        return res.body
      } else {
        // Check HTTP status code instead of body.status field
        if (res.status >= 400) {
          // Error response
          if (res.body?.error?.code === 'ID_NOT_FOUND') {
            try {
              ;(window as IRedirectingWindow).__REDIRECTING_TO_ERROR = true
              // Parallel requests can all 404; a merged record's survivor beats the generic error.
              const target = sameOriginPath(res.body.error.data?.redirectTo)
              if (target || !notFoundRedirect)
                notFoundRedirect = target ?? '/error?source=invalid_id'
              setTimeout(() => {
                if (notFoundRedirect) window.location.href = notFoundRedirect
              }, 0)

              return {}
            } catch {
              return {}
            }
          }
          // Handle HTTP 413 error (file too large) with user-friendly message
          if (res.status === 413) {
            throw Object.assign(
              new Error(
                `Your upload is too large (max ${humanReadableFileSize(MAX_FILE_SIZE, false, 1000).join(' ')} per file and in total). Please try smaller files or fewer files, or email the documents to ops@dwightfund.com.`,
              ),
              {
                code: 'FILE_TOO_LARGE',
                status: 413,
              },
            )
          }
          // Attach HTTP status to error for sagas/middleware
          const errorData: IApiErrorData = res.body?.error || {
            code: 'HTTP_ERROR',
            message: `HTTP ${res.status} Error`,
          }
          errorData.status = res.status
          errorData.tokenGeneration = sentWithGeneration
          const err = new Error(errorData.message)
          Object.assign(err, errorData)
          throw err
        }
        // Success response (status 200)
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return -- HTTP response boundary: res is `any` by design; api modules narrow the shape
        return res.body?.data
      }
    })
  }
}
